import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, Activity } from "lucide-react";
import { WHATSAPP_LINK } from "@/lib/constants";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate min-h-[100svh] overflow-hidden bg-[#071B34] pt-32 pb-24"
    >
      {/* Background layers */}
      <div className="hero-glow" aria-hidden="true" />
      <div className="intelligence-grid" aria-hidden="true" />
      <div className="intelligence-grid-fine" aria-hidden="true" />
      <div className="noise-overlay" aria-hidden="true" />

      {/* Decorative orbs */}
      <div className="absolute top-1/3 -left-24 w-[420px] h-[420px] rounded-full bg-[#14D6C7]/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#2EE8FF]/8 blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 flex flex-col items-center text-center">
        {/* Eyebrow */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp}
          custom={0}
          className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[#14D6C7]/25 bg-[#14D6C7]/5 backdrop-blur-sm mb-8"
        >
          <span className="pulse-dot" />
          <span className="text-[11px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[#14D6C7] font-manrope">
            Intelligence Infrastructure
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          data-testid="hero-headline"
          initial="hidden"
          animate="show"
          variants={fadeUp}
          custom={1}
          className="text-balance font-manrope font-bold tracking-[-0.025em] text-white text-[clamp(2.4rem,6vw,4.6rem)] leading-[1.05] max-w-5xl"
        >
          Intelligence Infrastructure{" "}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-[#14D6C7] via-[#2EE8FF] to-[#14D6C7] bg-clip-text text-transparent bg-[length:200%_100%] animate-shimmer">
              for Better Healthcare
            </span>
          </span>
        </motion.h1>

        {/* Subhead */}
        <motion.p
          initial="hidden"
          animate="show"
          variants={fadeUp}
          custom={2}
          className="mt-7 max-w-2xl text-balance font-poppins text-base sm:text-lg text-white/65 leading-relaxed"
        >
          Zeltaa is building the intelligence infrastructure that helps healthcare providers
          deliver faster diagnoses and better patient outcomes.
        </motion.p>

        <motion.p
          initial="hidden"
          animate="show"
          variants={fadeUp}
          custom={3}
          className="mt-4 max-w-2xl text-balance font-poppins text-sm sm:text-base text-white/50 leading-relaxed"
        >
          Our AI-powered systems augment clinical workflows, reduce operational bottlenecks,
          and enable healthcare organizations to scale expertise without increasing complexity.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp}
          custom={4}
          className="mt-10 flex flex-col sm:flex-row items-center gap-4"
        >
          <a
            data-testid="hero-explore-products-btn"
            href="#products"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/[0.04] border border-white/15 text-white text-sm font-semibold hover:bg-white/[0.08] hover:border-[#14D6C7]/40 transition-all duration-300 font-manrope backdrop-blur-sm"
          >
            Explore Products
            <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" strokeWidth={2.2} />
          </a>
          <a
            data-testid="hero-book-demo-btn"
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-shine group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#14D6C7] text-[#071B34] text-sm font-semibold hover:bg-[#2EE8FF] transition-colors duration-300 shadow-[0_0_0_1px_rgba(46,232,255,0.25),0_10px_40px_-10px_rgba(20,214,199,0.55)] font-manrope"
          >
            Book a Demo
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
          </a>
        </motion.div>

        {/* Floating metrics card */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-20 w-full max-w-5xl"
        >
          <div className="absolute inset-x-10 -top-6 h-12 bg-[#14D6C7]/20 blur-2xl rounded-full" aria-hidden="true" />
          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] backdrop-blur-md">
            {[
              { value: "10x", label: "Faster reports" },
              { value: "<60s", label: "Avg generation" },
              { value: "100%", label: "Template fidelity" },
              { value: "24/7", label: "Always-on" },
            ].map((m, i) => (
              <div
                key={m.label}
                className="relative px-6 py-7 bg-[#071B34]/60 text-left"
              >
                <div className="text-[#14D6C7] text-3xl font-bold font-manrope tracking-tight">
                  {m.value}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-white/45 font-poppins">
                  {m.label}
                </div>
                {i < 3 && (
                  <span className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-10 w-px bg-white/10" />
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-16 flex flex-col items-center gap-3 text-white/40"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] font-poppins">Scroll</span>
          <div className="scroll-hint" />
        </motion.div>
      </div>
    </section>
  );
}
